import type { SessionUser } from '@sr-ceo/contracts';
import { api } from './api.ts';

const BRAND =
  '<div class="brand"><div class="brand-mark">S</div><div><h1>Stackroll</h1><small>CEO Control Center</small></div></div>';

/** A screen over the dashboard. While it is up, the dashboard behind it is inert: no Tab stops, no
 *  pop-ups, nothing clickable. close() takes it away and gives the dashboard back. */
function overlay(inner: string) {
  const el = document.createElement('div');
  el.className = 'login-overlay';
  el.innerHTML = inner;
  const behind = [...document.body.children].filter((c): c is HTMLElement => c instanceof HTMLElement);
  for (const c of behind) {
    c.inert = true;
  }
  document.body.appendChild(el);
  const close = () => {
    el.remove();
    for (const c of behind) {
      c.inert = false;
    }
  };
  return { el, close };
}

/** Sign-in with a dashboard account, drawn with the mock's own card styles. Resolves once signed in. */
export function signIn(): Promise<SessionUser> {
  const { el, close } = overlay(`
    <form class="card login-card" novalidate>
      ${BRAND}
      <p class="login-hint">Sign in with your dashboard account.</p>
      <label for="login-user">Username</label>
      <input id="login-user" name="username" autocomplete="username" required>
      <label for="login-pass">Password</label>
      <input id="login-pass" name="password" type="password" autocomplete="current-password" required>
      <p class="login-error" role="alert" hidden></p>
      <button type="submit">Sign in</button>
    </form>`);
  const form = el.querySelector('form') as HTMLFormElement;
  const error = el.querySelector('.login-error') as HTMLElement;
  const button = el.querySelector('button') as HTMLButtonElement;
  (el.querySelector('#login-user') as HTMLInputElement).focus();

  return new Promise((resolve) => {
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      const data = new FormData(form);
      button.disabled = true;
      error.hidden = true;
      try {
        const user = await api.login(String(data.get('username') ?? ''), String(data.get('password') ?? ''));
        close();
        resolve(user);
      } catch (err) {
        error.textContent = (err as Error).message || 'Sign-in failed.';
        error.hidden = false;
        button.disabled = false;
      }
    });
  });
}

/** Shown when the dashboard service can't be reached at start-up; Retry reloads the page. */
export function showUnreachable(message: string) {
  const { el } = overlay(`
    <div class="card login-card" role="alert">
      ${BRAND}
      <p class="login-error"></p>
      <button type="button">Retry</button>
    </div>`);
  (el.querySelector('.login-error') as HTMLElement).textContent = message;
  (el.querySelector('button') as HTMLButtonElement).addEventListener('click', () => location.reload());
}

/** A small sign-out link under the sidebar note. */
export function addSignOut(user: SessionUser) {
  const note = document.querySelector('.side-note');
  if (!note) {
    return;
  }
  const link = document.createElement('a');
  link.className = 'sign-out';
  link.href = '#';
  link.textContent = `Sign out (${user.username})`;
  link.addEventListener('click', async (e) => {
    e.preventDefault();
    try {
      await api.logout();
      location.reload();
    } catch {
      // Still signed in: say so and leave the link to try again.
      link.textContent = `Sign-out failed, try again (${user.username})`;
    }
  });
  note.after(link);
}
